import { useCallback, useEffect } from 'react';
import { ArrowUpTrayIcon, BoltIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { atom, useAtom } from 'jotai';

import { UploadCard } from '../legacy';
import { Heading, Text, Button, Modal, ModalContent } from '@restworld/ui-ds-base';

import { useUploadCV } from './hooks/use-upload-cv';
import { EntryPointChecklistProvider, UploadedCVUI } from '../worker-entry-point';

export const showUploadCVModalAtom = atom<boolean>(false);

export const UploadWorkerCv = ({ showUploadCvButton = true }: { showUploadCvButton?: boolean }) => {
  const [showModal, setShowModal] = useAtom(showUploadCVModalAtom);
  const { t } = useTranslation();
  const onClose = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  return (
    <>
      {showUploadCvButton && (
        <Button
          StartIcon={ArrowUpTrayIcon}
          variant="secondary-neutral"
          className={'mt-0'}
          fullwidth={true}
          onClick={openModal}
        >
          {t('common:upload_cv')}
        </Button>
      )}

      <Modal open={showModal} onOpenChange={(v) => !v && onClose()}>
        <EntryPointChecklistProvider>
          <UploadCvForm {...{ onClose }} />
        </EntryPointChecklistProvider>
      </Modal>
    </>
  );
};

export const UploadCvForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const {
    onUploadFileChange,
    isCVUploaded,
    handleUploadFiles,
    doesSizeExceeds,
    uploadCVLoading,
    isCVNotUploaded,
    uploadFiles,
  } = useUploadCV();

  useEffect(() => {
    if (isCVUploaded) onClose();
  }, [isCVUploaded, onClose]);

  return (
    <ModalContent
      variant="center"
      className="lg:min-w-[31.75rem] lg:max-w-[31.75rem] bg-gray-50"
      footer={
        // Keep the Continue/Cancel actions pinned in the fixed footer so they stay in view while
        // the upload body (dropzone → file preview) scrolls.
        <div className="flex flex-col items-center w-full gap-4">
          <Button
            type="submit"
            loading={uploadCVLoading}
            fullwidth={true}
            disabled={!uploadFiles?.length || doesSizeExceeds}
            onClick={() => onUploadFileChange()}
          >
            {t('common:continue')}
          </Button>
          <Button variant="secondary-neutral" fullwidth={true} onClick={onClose}>
            {t('common:cancel')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center w-full">
        <Heading size={2} className={`text-center ${!uploadFiles ? 'mb-8' : 'mb-2'}`}>
          {!uploadFiles
            ? t('onboarding-page:upload_your_cv')
            : t('onboarding-page:complete_your_profile_by_uploading_your_cv')}
        </Heading>
        {!!uploadFiles?.length && (
          <div className="flex flex-row gap-2 items-center mb-10">
            <BoltIcon className="w-5 h-5 text-primary-800" />
            <Text color="muted">{t('onboarding-page:it_takes_less_than45_seconds')}</Text>
          </div>
        )}
        {!uploadFiles?.length ? (
          <UploadCard
            isError={isCVNotUploaded}
            setUploadFiles={handleUploadFiles}
            fileType={['.pdf', '.doc', '.docx', '.jpg']}
          />
        ) : (
          <UploadedCVUI />
        )}
      </div>
    </ModalContent>
  );
};
